(function () {
	const SELECTOR = '[data-transcript-crawl]';

	const prefersReducedMotion = () =>
		window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	const stopLoop = (root) => {
		if (!root) return;
		if (root._crawlRaf) {
			cancelAnimationFrame(root._crawlRaf);
			root._crawlRaf = null;
		}
		root._crawlRunning = false;
	};

	const ensureCopies = (root) => {
		const viewport = root.querySelector('[data-transcript-crawl-viewport]');
		const track = root.querySelector('[data-transcript-crawl-track]');
		const source = root.querySelector('[data-transcript-crawl-source]');

		if (!viewport || !track || !source) return null;

		const segmentHeight = source.offsetHeight;
		if (!segmentHeight) return null;

		// Enough copies to cover ~2.5 viewports so the loop never exposes empty space
		const needed = Math.max(
			3,
			Math.ceil((viewport.clientHeight * 2.5) / segmentHeight) + 1
		);

		const existing = track.querySelectorAll('[data-transcript-crawl-segment]').length;

		for (let i = existing; i < needed; i += 1) {
			const clone = source.cloneNode(true);
			clone.removeAttribute('data-transcript-crawl-source');
			clone.setAttribute('aria-hidden', 'true');
			track.appendChild(clone);
		}

		// Trim extras after resize if we overshot significantly
		const segments = track.querySelectorAll('[data-transcript-crawl-segment]');
		if (segments.length > needed + 2) {
			for (let i = segments.length - 1; i >= needed; i -= 1) {
				if (!segments[i].hasAttribute('data-transcript-crawl-source')) {
					segments[i].remove();
				}
			}
		}

		return {
			track,
			viewport,
			source,
			segmentHeight: source.offsetHeight,
		};
	};

	const applyTransform = (track, offset) => {
		track.style.transform = `translate3d(0, ${-offset}px, 0)`;
	};

	const tick = (root, now) => {
		if (!root._crawlRunning) return;

		const speed = root._crawlSpeed || 20;
		const segmentHeight = root._crawlSegmentHeight || 0;
		const track = root._crawlTrack;

		if (!track || !segmentHeight) {
			root._crawlRaf = requestAnimationFrame((t) => tick(root, t));
			return;
		}

		const last = root._crawlLastTime || now;
		const delta = Math.min((now - last) / 1000, 0.064); // clamp large tab-blur gaps
		root._crawlLastTime = now;

		if (!root._crawlPaused) {
			let offset = (root._crawlOffset || 0) + speed * delta;
			// Modular wrap — identical segments mean this is visually seamless
			if (offset >= segmentHeight) {
				offset = offset % segmentHeight;
			}
			root._crawlOffset = offset;
			applyTransform(track, offset);
		}

		root._crawlRaf = requestAnimationFrame((t) => tick(root, t));
	};

	const startLoop = (root) => {
		if (!root || prefersReducedMotion()) {
			stopLoop(root);
			const track = root && root.querySelector('[data-transcript-crawl-track]');
			if (track) applyTransform(track, 0);
			return;
		}

		const layout = ensureCopies(root);
		if (!layout) return;

		root._crawlTrack = layout.track;
		root._crawlSegmentHeight = layout.segmentHeight;
		root._crawlSpeed = parseFloat(root.dataset.crawlSpeed) || 20;
		root._crawlOffset = (root._crawlOffset || 0) % layout.segmentHeight;

		applyTransform(layout.track, root._crawlOffset);

		if (!root._crawlRunning) {
			root._crawlRunning = true;
			root._crawlLastTime = null;
			root._crawlRaf = requestAnimationFrame((t) => tick(root, t));
		}
	};

	const bindPauseOnHover = (root, track) => {
		if (!track) return;
		if (root.dataset.crawlHoverBound === 'true') return;
		if (!track.classList.contains('transcript-crawl__track--paused')) return;

		root.dataset.crawlHoverBound = 'true';

		track.addEventListener('mouseenter', () => {
			root._crawlPaused = true;
		});
		track.addEventListener('mouseleave', () => {
			root._crawlPaused = false;
			root._crawlLastTime = null;
		});
	};

	const escapeHtml = (value) =>
		String(value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');

	const setCrawlEmpty = (root, isEmpty) => {
		const empty = root.querySelector('[data-transcript-crawl-empty]');
		const track = root.querySelector('[data-transcript-crawl-track]');
		if (empty) empty.hidden = !isEmpty;
		if (track) track.classList.toggle('is-empty', Boolean(isEmpty));
	};

	const resetTrack = (root) => {
		const track = root.querySelector('[data-transcript-crawl-track]');
		const source = root.querySelector('[data-transcript-crawl-source]');
		if (!track || !source) return;

		stopLoop(root);
		track.querySelectorAll('[data-transcript-crawl-segment]').forEach((segment) => {
			if (segment !== source) segment.remove();
		});
		root._crawlOffset = 0;
		applyTransform(track, 0);
	};

	const applyTranscriptsToCrawl = (root, items) => {
		const source = root.querySelector('[data-transcript-crawl-source]');
		const sepNode = root.querySelector('[data-transcript-crawl-sep]');
		if (!source) return;

		const sepHtml = sepNode
			? `<span class="transcript-crawl__sep" aria-hidden="true">${sepNode.innerHTML}</span>`
			: '<span class="transcript-crawl__sep" aria-hidden="true">●</span>';

		resetTrack(root);

		if (!items.length) {
			source.innerHTML = '';
			setCrawlEmpty(root, true);
			return;
		}

		source.innerHTML = items.map(escapeHtml).join(sepHtml) + sepHtml;
		setCrawlEmpty(root, false);
	};

	const loadCsvIfNeeded = (root) => {
		const url = (root.dataset.transcriptsCsvUrl || '').trim();
		if (!url || !window.TranscriptsData) return Promise.resolve(false);

		const sampleSize = parseInt(root.dataset.transcriptsCsvSample, 10) || 100;

		return window.TranscriptsData.fetchTranscripts(url)
			.then((items) => {
				if (!items.length) return false;
				applyTranscriptsToCrawl(root, window.TranscriptsData.sample(items, sampleSize));
				return true;
			})
			.catch(() => false);
	};

	const bindResizeObserver = (root) => {
		if (root._crawlResizeObserver) return;
		const source = root.querySelector('[data-transcript-crawl-source]');
		if (!source || typeof ResizeObserver === 'undefined') return;

		let resizeTimer;
		const observer = new ResizeObserver(() => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => startLoop(root), 120);
		});
		observer.observe(source);
		const viewport = root.querySelector('[data-transcript-crawl-viewport]');
		if (viewport) observer.observe(viewport);
		root._crawlResizeObserver = observer;
	};

	const initRoot = (root) => {
		if (!root || root.hasAttribute('data-transcript-crawl-managed')) return;

		const track = root.querySelector('[data-transcript-crawl-track]');
		if (!track) return;

		const boot = () => {
			const source = root.querySelector('[data-transcript-crawl-source]');
			const hasText = Boolean(source && source.textContent.trim());
			setCrawlEmpty(root, !hasText);
			if (!hasText) return;

			startLoop(root);
			bindPauseOnHover(root, track);
			bindResizeObserver(root);
		};

		if (root.dataset.crawlReady === 'true') {
			boot();
			return;
		}
		root.dataset.crawlReady = 'true';

		const source = root.querySelector('[data-transcript-crawl-source]');
		const hasFallback = Boolean(source && source.textContent.trim());
		if (hasFallback) boot();

		loadCsvIfNeeded(root).then((loaded) => {
			if (loaded) boot();
			else if (!hasFallback) boot();
		});
	};

	const initAll = () => {
		document.querySelectorAll(SELECTOR).forEach(initRoot);
	};

	window.TranscriptCrawl = {
		fill(root, items) {
			if (!root) return;
			applyTranscriptsToCrawl(root, Array.isArray(items) ? items : []);
			const track = root.querySelector('[data-transcript-crawl-track]');
			bindPauseOnHover(root, track);
			bindResizeObserver(root);
		},
		start(root) {
			if (!root) return;
			startLoop(root);
		},
		stop(root) {
			stopLoop(root);
		},
	};

	let resizeTimer;
	const onResize = () => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(() => {
			document.querySelectorAll(SELECTOR).forEach((root) => {
				if (root.hasAttribute('data-transcript-crawl-managed') && root.closest('.is-searching')) {
					return;
				}
				startLoop(root);
			});
		}, 150);
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initAll);
	} else {
		initAll();
	}

	window.addEventListener('resize', onResize);

	document.addEventListener('shopify:section:load', () => {
		setTimeout(initAll, 100);
	});

	document.addEventListener('shopify:section:unload', (event) => {
		const root = event.target.querySelector(SELECTOR);
		if (!root) return;
		stopLoop(root);
		if (root._crawlResizeObserver) {
			root._crawlResizeObserver.disconnect();
			delete root._crawlResizeObserver;
		}
	});

	document.addEventListener('visibilitychange', () => {
		document.querySelectorAll(SELECTOR).forEach((root) => {
			if (root.hasAttribute('data-transcript-crawl-managed') && root.closest('.is-searching')) {
				return;
			}
			if (document.hidden) {
				root._crawlLastTime = null;
			} else {
				root._crawlLastTime = null;
				startLoop(root);
			}
		});
	});
})();
